/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { Fragment, useMemo, useState } from 'react'
import { observer } from 'mobx-react'
import _ from 'lodash'
import uuid from 'uuid'
import { isMobile } from 'react-device-detect'
import { Button } from '@styles/button.style'
import {
  formatCreatedAtToElapsedTime,
  isNotStartedMatchStatus,
  makeAttachmentDetailObjToBettingRecordTrack,
  showToast,
  TOAST_TYPE,
} from '@utils/utils'
import useRootStore from '@store/useRootStore'
import { UserPickSpecialMemberMark } from '@views/Home/UserPick/Components/UserPickSpecialMemberMark'
import { Popover } from 'antd'
import CommentAddImg from '@assets/images/btn/comment-add-btn.png'
import { SimpleCheerForTeamMatchInfo } from '@components/SimpleCheerForTeamMatchInfo'
import { CHEER_FOR_TEAM_CONTENTS_TYPE } from '@v2/constants/cheer'
import SimpleBettingHistory from '@components/SimpleBettingHistory'
import { CheerForTeamListItem } from '@views/Matchup/MatchUpDetail/index.style'
import PopupUtils from '@utils/popup-utils'
import { UserPickPopoverWrap } from '@views/Home/index.style'
import DialogUtils from '@utils/dialog-utils'
import { COMMUNICATION_BLOCK_TYPE, GlobalStoreKey, USER_REPORT_TYPE } from '@v2/constants/common'
import { Schema$CheerForTeam } from '@interface/cheer-for-team'
import ConvertUtils from '@utils/convert-utils'
import dayjs from 'dayjs'
import { BASIC_TEAM_EMBLEM_MAP } from '@shared/EmblemGroup'
import classNames from 'classnames'
import { TIMELINE_POPUP_ENABLED_SPORT_IDS } from '@game-type/game-market-types'
import AdvImg from '@components/AdvImg'
import { makeATagAutoGenerationElements } from '@utils/jsxutils'
import { matchupStore } from '@store/index'

export interface SportTalkItemItemProps {
  sportTalk: Schema$CheerForTeam,
  fetchCheerForTeams: () => void
  showSimpleCheerForTeamInfo?: boolean
}

export const SportTalkItem: React.FC<SportTalkItemItemProps> = observer((props) => {
  const { cheerForTeamStore, authStore, globalStore, userStore } = useRootStore()
  const { sportTalk, fetchCheerForTeams, showSimpleCheerForTeamInfo } = props
  const { owner } = sportTalk
  let { effectEnabled } = sportTalk

  const [addSuffixList, setAddSuffixList] = useState('[]')
  const [isSocialMedia, setIsSocialMedia] = useState(false)

  const sportsTalkContent = useMemo(() => {
    let content: string
    if (sportTalk.filteredContents) {
      content = sportTalk.filteredContents
    } else {
      content = sportTalk.contents
    }

    return (
      makeATagAutoGenerationElements(globalStore, content, isSocialMedia, () => {
        if (!isSocialMedia) {
          setIsSocialMedia(true)
        }
      })
    )
  }, [isSocialMedia, sportTalk])

  const isMine = owner.userPromiseId === authStore.userInfo.id
  const isUserBlockEnabled = globalStore.getUserBlockEnabled()
  const isPossibleUserBlock = globalStore.isPossibleUserBlockByUserPromiseId(
    owner.userPromiseId,
  )
  const isBlockUser = owner.isBlocked

  const blockUserSuccessCallBack = (userPromiseId: string, blockTypes: COMMUNICATION_BLOCK_TYPE[]) => {
    cheerForTeamStore.setCheerForTeamsUserBlock(userPromiseId, blockTypes)
  }

  const unBlockUserSuccessCallBack = (userPromiseId: string, blockTypes: COMMUNICATION_BLOCK_TYPE[]) => {
    cheerForTeamStore.setCheerForTeamsUserBlock(userPromiseId, blockTypes)
  }

  const onClickBlockUser = () => {
    const { userPromiseId, userNick, userProfileImg } = owner
    if (!isBlockUser) {
      DialogUtils.showUserBlock(userPromiseId, userNick, userProfileImg, blockUserSuccessCallBack)
    } else {
      DialogUtils.showUserUnBlock(userPromiseId, unBlockUserSuccessCallBack)
    }
  }

  const onClickReportComment = (cheerForTeam: Schema$CheerForTeam) => {
    if (cheerForTeam.reported) {
      return
    }

    DialogUtils.showUserReport({
      reportType: USER_REPORT_TYPE.CHEER_FOR_TEAM,
      targetId: _.toString(cheerForTeam.id),
      targetUserPromiseId: cheerForTeam.owner.userPromiseId,
      callbackReport: (result) => {
        if (result) {
          cheerForTeamStore.setAttributesByCheerForTeamId({ cheerForTeamId: cheerForTeam.id, reported: result })
        }
      },
    })
  }
  const onClickUserDetailInfoPopup = (cheerForTeam: Schema$CheerForTeam) => {
    if (authStore.isSignedIn) {
      PopupUtils.showUserDetailInfo(cheerForTeam.owner.userPromiseId)
    }
  }

  const onClickCheerForTeamDelete = async (cheerForTeamId: number, matchId: number) => {
    DialogUtils.showDeleteSportTalk(async () => {
      try {
        await cheerForTeamStore.deleteCheerForTeam(cheerForTeamId)
        matchupStore.updateMatchCheerForTeamDeleted(matchId)
        showToast(TOAST_TYPE.INFO, '삭제되었습니다.')
        fetchCheerForTeams()

        userStore.decreaseMyActivityTotalCheerForTeam()
        if (dayjs().isSame(sportTalk.createdAt, 'months')) {
          userStore.decreaseMyActivityMonthlyCheerForTeam()
        }
      } catch (err) {
        console.error(err)
      }
    })
  }

  const onClickViewImg = (imageSrc: string) => {
    DialogUtils.showImagePreview({ imageSrc })
  }
  const attachImgUrlPrefix = globalStore.findGlobalSetting(GlobalStoreKey.CHAT_ATTACH_IMAGE_URL_PREFIX)?.value
  const attachEmoticonUrlPrefix = globalStore.findGlobalSetting(GlobalStoreKey.CHAT_EMOTICON_URL_PREFIX)?.value

  const onClickCheerForTeamInfo = () => {
    const isTimelinePopupEnabled = TIMELINE_POPUP_ENABLED_SPORT_IDS.includes(sportTalk.sportId)
    if (!isTimelinePopupEnabled) {
      return
    }

    const matchInfo
      = ConvertUtils.converterCheerForTeamDataToMatchRecordSimpleMatchInfo({ cheerForTeamData: sportTalk })
    DialogUtils.showMatchRecord(matchInfo)
  }

  const onClickOpenBettingListInfo = async (betId: string, fromUserNick: string) => {
    DialogUtils.showSharedBettingList({ betId, fromUserNick })
  }

  const addEmoticonSuffixHandler = (e, itemId: string): void => {
    e.stopPropagation()
    const suffixObj = JSON.parse(addSuffixList)
    let suffix = _.find(suffixObj, { itemId })
    if (suffix) {
      suffix.suffix = uuid.v4()
      _.remove(suffixObj, { itemId })
    } else {
      suffix = { itemId, suffix: uuid.v4() }
    }
    suffixObj.push(suffix)
    setAddSuffixList(JSON.stringify(suffixObj))
  }

  const makeEmoticonAttachmentUrl = () => {
    const attachment = sportTalk.attachment
    const itemId = sportTalk.id
    const suffixObj = JSON.parse(addSuffixList)
    const suffix = _.find(suffixObj, { itemId })
    return `${attachEmoticonUrlPrefix}${attachment}?i=${itemId}&t=${suffix ? suffix.suffix : uuid.v4()}`
  }

  let isShowSimpleScoreInfo = !effectEnabled

  if (isNotStartedMatchStatus(sportTalk.matchStatus)) {
    isShowSimpleScoreInfo = false
  }

  if (showSimpleCheerForTeamInfo) {
    effectEnabled = true
    isShowSimpleScoreInfo = false
  }

  const cheerForTeamBtnClassName = classNames('cheer-for-team-btn', {
    'pc': !isMobile,
    'adv-hidden': !effectEnabled,
  })

  const popoverContent = (
    <UserPickPopoverWrap>
      {!isMine ? (
          <Fragment>
            <Button TP className="popover-btn" onClick={() => {
              onClickReportComment(sportTalk)
            }}>
              {sportTalk.reported ? '신고완료' : '신고하기'}
            </Button>
            {isUserBlockEnabled && isPossibleUserBlock && (
              <Button TP className={isBlockUser ? 'popover-btn delete' : 'popover-btn'} onClick={onClickBlockUser}>
                {isBlockUser ? '차단해제' : '차단하기'}
              </Button>
            )}
          </Fragment>
        )
        :
        (
          <Fragment>
            <Button TP className="popover-btn delete" onClick={() => {
              onClickCheerForTeamDelete(sportTalk.id, sportTalk.matchId).catch(console.error)
            }}>
              삭제하기
            </Button>
          </Fragment>
        )
      }
    </UserPickPopoverWrap>
  )

  return (
    <CheerForTeamListItem className={!isMobile && 'pc'} key={`cheerForTeamListItem_${sportTalk.id}`}>
      <Button
        className={isBlockUser ? 'user-profile-btn blocked' : 'user-profile-btn'}
        onClick={() => {
          onClickUserDetailInfoPopup(sportTalk)
        }}
      >
        <div className="profile-img-wrap lhn24">
          {owner.isCustomProfileImg === true ?
            (<AdvImg src={owner.userProfileImg} srcMode={'small'} alt="사진첩 프로필" className="profile-img custom-img"/>)
            :
            (<AdvImg src={owner.userProfileImg} srcMode={'small'} alt="스킨 프로필" className="profile-img"/>)
          }
        </div>
        <UserPickSpecialMemberMark
          showSMark={owner.hasSpecialMark}
          isSpecialMember={owner.isSpecialMember}
          userTitleId={owner.userTitleId}
          userTitleEnabled={owner.userTitleEnabled}
          tiny
          darkBorder
        />
      </Button>
      <div className="contents-wrap">
        <div className={isBlockUser ? 'nick-name blocked' : 'nick-name'}>
          {owner.userNick}
          {isShowSimpleScoreInfo &&
            <div className="score-info-wrap">
              <span className="score">
                <span className="emblem">
                  <img src={sportTalk.homeEmblem || BASIC_TEAM_EMBLEM_MAP[sportTalk.sportId]} alt="홈팀 엠블럼"
                       loading="lazy"/>
                </span>
                {sportTalk.homeScore}
              </span>
              <span className="center">:</span>
              <span className="score">
                {sportTalk.awayScore}
                <span className="emblem">
                  <img src={sportTalk.awayEmblem || BASIC_TEAM_EMBLEM_MAP[sportTalk.sportId]} alt="원정팀 엠블럼"
                       loading="lazy"/>
                </span>
              </span>
            </div>
          }
          <p className="date">{formatCreatedAtToElapsedTime({
            createdAt: sportTalk.createdAt,
            modifiedAt: null,
          })}</p>
          {authStore.isSignedIn &&
            <div>
              <Popover
                placement="bottomLeft"
                content={popoverContent}
                trigger={isMobile ? 'click' : 'hover'}
                mouseLeaveDelay={0.05}
                overlayClassName={'user-comment-menu'}
                getPopupContainer={(triggerNode: any) => triggerNode.parentNode}
              >
                <Button TP className="cheer-extra-btn">
                  <img src={CommentAddImg} alt="추가버튼"/>
                </Button>
              </Popover>
            </div>
          }
        </div>
        <div key={`cheerForTeamListItem_block_${sportTalk.id}`} className="contents">
          {isBlockUser ?
            <p className={isBlockUser ? 'sport-talk-contents blocked' : 'sport-talk-contents'}>차단된 유저의 스포츠톡입니다.</p>
            :
            <Fragment>
              <Button
                className={cheerForTeamBtnClassName}
                onClick={onClickCheerForTeamInfo}
              />
              {Boolean(effectEnabled) &&
                <SimpleCheerForTeamMatchInfo darkBg selectedCheerForTeamData={sportTalk}/>
              }

              {sportTalk.contentsType === CHEER_FOR_TEAM_CONTENTS_TYPE.IMAGE && (
                <div className="contents--upload">
                  <Button
                    className="attachment-img-btn"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      onClickViewImg(`${attachImgUrlPrefix + sportTalk.attachment}`)
                      return false
                    }}>
                    <AdvImg src={`${attachImgUrlPrefix + sportTalk.attachment}`} srcMode={'small'} alt="업로드 이미지" className="upload-img-preview lhn6" loading="lazy"/>
                  </Button>
                </div>
              )}
              {sportTalk.contentsType === CHEER_FOR_TEAM_CONTENTS_TYPE.EMOTICON &&
                <Button className="contents--upload emoticon">
                  <img
                    src={makeEmoticonAttachmentUrl()}
                    alt="이모티콘"
                    className="upload-img-preview emoticon"
                    loading="lazy"
                    onClick={(e) => addEmoticonSuffixHandler(e, _.toString(sportTalk.id))}
                  />
                </Button>
              }
              {(sportTalk.contentsType === CHEER_FOR_TEAM_CONTENTS_TYPE.BET_HISTORY
                  || sportTalk.contentsType === CHEER_FOR_TEAM_CONTENTS_TYPE.AUTO_GENERATE_BET_HISTORY) &&
                /** 공유한 베팅내역 첨부해주시면 됩니다 */
                <div className="contents--upload">
                  <SimpleBettingHistory
                    // isFold // TODO: fold 베팅내역일 경우
                    bettingRecordTrack={makeAttachmentDetailObjToBettingRecordTrack(
                      JSON.parse(sportTalk.attachmentDetail))}
                    onClickOpenBettingListInfo={() => onClickOpenBettingListInfo(sportTalk.attachment,
                      owner.userNick)}
                    className={!isMobile ? 'detail-bet-betting-list detail-bet-list pc' : 'detail-bet-betting-list detail-bet-list'}
                  />
                </div>
              }

              <p className={isMobile ? 'sport-talk-contents' : 'sport-talk-contents pc'}>{sportsTalkContent}</p>
            </Fragment>
          }
        </div>
      </div>
    </CheerForTeamListItem>
  )
})
